// fetchLedger - one count of every model-byte request the viewer sends
// (tiles, sidecars, packs, fragment GLBs), and one in-flight promise per URL.
//
// WHY (round 10, 2026-10-14). Tom's real-GPU cold loads of SpaceX showed
// about two requests per pack URL (5,950 requests for 2,880 URLs). The
// headless staging count on main.a0d9b5d5 agreed - 4,057 pack requests for
// 2,477 URLs - and said where they came from: NONE of the repeats
// overlapped the first request (the in-flight sharing worked); every
// repeat came 2.6-60 s later, after the fragment the pack had prefilled was
// evicted from the library cache by its 8,000-entry cap, and the IndexedDB
// re-read then timed out under a busy main thread. The fixes are in
// streamingController (entry cap) and GLBFetcher (re-read wait); this file
// is what lets anyone see the number move: window.__sl.perfSnapshot().fetches.
//
// "duplicate" means a request for a URL that has ALREADY been answered
// successfully this session. A retry after a failure is not a duplicate
// (it is counted under `retries`), and neither is a caller joining a
// request that is still in flight (`shared`): neither costs a second
// download of bytes we hold.
//
// Main thread only: no worker fetches model bytes (GLTFWorkerPool and
// PackDecodePool are handed buffers), so one module-level ledger and one
// in-flight map see every request.

const TOP_N = 10;

function kindOf(url) {
  const u = String(url || '');
  if (/\/packs\d*\//.test(u)) return 'pack';
  if (/\/fragments\//.test(u)) return 'fragment';
  if (/\/tiles\//.test(u)) return 'tile';
  return 'other';
}

export class FetchLedger {
  constructor() { this.reset(); }

  reset() {
    this._urls = new Map();      // url -> { requests, ok, failed, bytes }
    this.requests = 0;
    this.duplicates = 0;
    this.retries = 0;
    this.failures = 0;
    this.shared = 0;
    this.bytes = 0;
    this.inFlight = 0;
    this.inFlightPeak = 0;
    this.byKind = {};
  }

  _entry(url) {
    let e = this._urls.get(url);
    if (!e) { e = { requests: 0, ok: 0, failed: 0, bytes: 0 }; this._urls.set(url, e); }
    return e;
  }

  // A network request is about to go out. `retry`: an attempt after a
  // failed one for the same call.
  begin(url, retry = false) {
    const e = this._entry(url);
    const k = this.byKind[kindOf(url)] || (this.byKind[kindOf(url)] = { requests: 0, duplicates: 0, bytes: 0 });
    e.requests++;
    k.requests++;
    this.requests++;
    if (retry) this.retries++;
    else if (e.ok > 0) { this.duplicates++; k.duplicates++; }
    this.inFlight++;
    if (this.inFlight > this.inFlightPeak) this.inFlightPeak = this.inFlight;
  }

  // The request settled. `bytes` only when the body was read.
  end(url, ok, bytes = 0) {
    const e = this._entry(url);
    this.inFlight = Math.max(0, this.inFlight - 1);
    if (ok) {
      e.ok++;
      e.bytes += bytes || 0;
      this.bytes += bytes || 0;
      const k = this.byKind[kindOf(url)];
      if (k) k.bytes += bytes || 0;
    } else {
      e.failed++;
      this.failures++;
    }
  }

  join() { this.shared++; }

  // Requests per URL, for a console or a headless run.
  byUrl() {
    const out = {};
    this._urls.forEach((e, url) => { out[url] = e.requests; });
    return out;
  }

  summary(topN = TOP_N) {
    const hist = {};
    let dupUrls = 0;
    const top = [];
    this._urls.forEach((e, url) => {
      hist[e.requests] = (hist[e.requests] || 0) + 1;
      if (e.requests > 1) { dupUrls++; top.push([url, e.requests]); }
    });
    top.sort((a, b) => b[1] - a[1]);
    return {
      requests: this.requests,
      uniqueUrls: this._urls.size,
      duplicates: this.duplicates,
      duplicateUrls: dupUrls,
      retries: this.retries,
      failures: this.failures,
      shared: this.shared,
      bytes: this.bytes,
      inFlight: this.inFlight,
      inFlightPeak: this.inFlightPeak,
      byKind: JSON.parse(JSON.stringify(this.byKind)),
      requestsPerUrl: hist,
      topRepeated: top.slice(0, topN),
    };
  }
}

const ledger = new FetchLedger();
export default ledger;

// One promise per key while it is in flight. Every caller after the first
// joins it; when it settles - resolved or rejected - the key is free again,
// so a failed or aborted fetch is never handed to the next caller and a
// retry goes back to the network. A joiner gets its own copy of an
// ArrayBuffer result so a consumer that transfers its buffer to a worker
// cannot detach anyone else's.
const _inFlight = new Map();

// Across realms too (a buffer made in a worker or a test sandbox).
function isArrayBuffer(v) {
  return Object.prototype.toString.call(v) === '[object ArrayBuffer]';
}

export function shareInFlight(key, start, book = ledger) {
  const pending = _inFlight.get(key);
  if (pending) {
    if (book) book.join(key);
    // Chained on the underlying promise, so the copy is taken before the
    // first caller's continuation can run (and transfer its buffer).
    // A joiner whose shared request was CANCELLED (nobody else wanted it
    // any more - slotQueue drops stale demand) still wants the bytes:
    // it starts its own request rather than inherit the cancellation.
    return pending.source.then(
      (v) => (isArrayBuffer(v) ? v.slice(0) : v),
      (err) => {
        if (!(err && err.cancelled)) throw err;
        // The cancelled entry is settled; never join it again (a loop).
        if (_inFlight.get(key) === pending) _inFlight.delete(key);
        return shareInFlight(key, start, book);
      }
    );
  }
  let source;
  try { source = Promise.resolve(start()); } catch (err) { source = Promise.reject(err); }
  const entry = { source };
  const free = () => { if (_inFlight.get(key) === entry) _inFlight.delete(key); };
  const settled = source.then((v) => { free(); return v; }, (err) => { free(); throw err; });
  _inFlight.set(key, entry);
  return settled;
}

export function inFlightSize() { return _inFlight.size; }
